import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { parseWarehouseError } from '../api/warehouse.api';
import type { FieldError } from '../types';

/** Where a (server or local) error belongs on a form. */
export interface FieldTarget<K extends string> {
  key: K;
  /** Line row index for line-level errors; null/undefined = a form field. */
  line?: number | null;
}

/**
 * Normalise anything a submit threw into `{field, message}` items:
 *  - VALIDATION `errors[]` as sent;
 *  - class-validator style (every message joined with "; ") split back up;
 *  - a code-only error (e.g. VENDOR_INACTIVE) pinned to the field `codeField`
 *    names for it, so it lands inline and moves the wizard to that step.
 * `list` is empty when the error is not field-shaped — show `message`.
 */
export const fieldErrorsOf = (
  err: unknown,
  codeField: Readonly<Record<string, string>> = {}
): { list: FieldError[]; message: string } => {
  const e = parseWarehouseError(err);
  let list = e.fieldErrors;
  if (!list.length && e.code === 'VALIDATION' && e.message.includes('; ')) {
    list = e.message.split('; ').map((m) => ({ field: '', message: m }));
  }
  if (!list.length && e.code && codeField[e.code]) {
    list = [{ field: codeField[e.code], message: e.message }];
  }
  // A code error whose details only name lines (e.g. PO SKU_NOT_IN_CATALOG:
  // `{rule, sku, lineIndex}`) → its message at each of those lines.
  if (!list.length && e.code) {
    list = e.errors.flatMap((d) => {
      const i = (d as { lineIndex?: unknown } | null)?.lineIndex;
      return typeof i === 'number'
        ? [{ field: `lines[${i}]`, message: e.message, lineIndex: i }]
        : [];
    });
  }
  return { list, message: e.message };
};

/**
 * Row errors of a bulk import 422 (`rows[i].<field>`, lineIndex i) by row
 * index, with the field reduced to its name (`weightKg`). Empty when the
 * error is not row-shaped.
 */
export const rowErrorsOf = (err: unknown): Map<number, FieldError[]> => {
  const out = new Map<number, FieldError[]>();
  parseWarehouseError(err).fieldErrors.forEach((e) => {
    const m = /^rows?\[(\d+)\]\.?(.*)$/.exec(e.field);
    const index =
      e.lineIndex !== undefined && e.lineIndex !== null
        ? e.lineIndex
        : m
          ? Number(m[1])
          : null;
    if (index === null) return;
    const list = out.get(index) ?? [];
    list.push({ ...e, field: m ? m[2] : e.field, lineIndex: index });
    out.set(index, list);
  });
  return out;
};

/** First message per field and per line row. */
export const indexFieldErrors = <K extends string>(
  errors: FieldError[],
  fieldOf: (e: FieldError) => FieldTarget<K>
): { field: Map<K, string>; line: Map<number, string> } => {
  const field = new Map<K, string>();
  const line = new Map<number, string>();
  errors.forEach((e) => {
    const t = fieldOf(e);
    if (t.line !== null && t.line !== undefined) {
      if (!line.has(t.line)) line.set(t.line, e.message);
    } else if (!field.has(t.key)) field.set(t.key, e.message);
  });
  return { field, line };
};

export interface ServerErrors<K extends string> {
  /** Every error shown in the summary alert. */
  errors: FieldError[];
  setErrors: Dispatch<SetStateAction<FieldError[]>>;
  clear: () => void;
  /** Inline message for a form field, or null. */
  fieldError: (key: K) => string | null;
  /** Inline message for a line row, or null. */
  lineError: (line: number) => string | null;
  /** The wizard step an error belongs to. */
  stepOfError: (e: FieldError) => number;
  /**
   * Show a failed submit. Field-shaped errors replace the list and the
   * earliest step that owns one is returned (jump there); otherwise the
   * list is cleared and `message` is returned for a form-level alert.
   */
  fromError: (
    err: unknown,
    codeField?: Readonly<Record<string, string>>
  ) => { step: number; message: null } | { step: null; message: string };
}

/**
 * One error model for the WMS wizards (outbound request, purchase order,
 * goods receipt): a 422 shows every item at once — in the summary and inline
 * at its field / line — and the form jumps back to the earliest step that
 * has one. `fieldOf` maps a backend field name onto a form key; `stepOf`
 * says which step owns that key. Pass module-level functions (stable).
 */
export function useServerErrors<K extends string>(
  fieldOf: (e: FieldError) => FieldTarget<K>,
  stepOf: (key: K) => number
): ServerErrors<K> {
  const [errors, setErrors] = useState<FieldError[]>([]);
  const index = useMemo(
    () => indexFieldErrors(errors, fieldOf),
    [errors, fieldOf]
  );

  const fieldError = useCallback(
    (key: K) => index.field.get(key) ?? null,
    [index]
  );
  const lineError = useCallback(
    (line: number) => index.line.get(line) ?? null,
    [index]
  );
  const stepOfError = useCallback(
    (e: FieldError) => stepOf(fieldOf(e).key),
    [fieldOf, stepOf]
  );
  const clear = useCallback(() => setErrors([]), []);

  const fromError = useCallback<ServerErrors<K>['fromError']>(
    (err, codeField) => {
      const { list, message } = fieldErrorsOf(err, codeField);
      if (list.length) {
        setErrors(list);
        return { step: Math.min(...list.map(stepOfError)), message: null };
      }
      setErrors([]);
      return { step: null, message };
    },
    [stepOfError]
  );

  return {
    errors,
    setErrors,
    clear,
    fieldError,
    lineError,
    stepOfError,
    fromError,
  };
}
